<template>
    <div class="article-detail">
        <!-- 阅读进度条 -->
        <div class="reading-progress-bar" :style="{ width: readingProgress + '%' }"></div>



        <div class="article-container">
            <!-- 返回按钮 -->
            <div class="back-nav">
                <button @click="goBack" class="back-btn">
                    ← 返回首页
                </button>
            </div>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading">
                <p>正在加载文章...</p>
            </div>

            <!-- 错误状态 -->
            <div v-else-if="error" class="error">
                <h2>{{ error }}</h2>
                <button @click="goBack" class="back-btn">返回首页</button>
            </div>

            <!-- 文章内容 -->
            <article class="article-content" v-else-if="article">
                <!-- 文章封面头部 -->
                <header class="article-hero">
                    <!-- 封面图片背景 -->
                    <div class="hero-background">
                        <div class="hero-image-container" v-if="article.cover_image_url">
                            <CachedImage :src="article.cover_image_url" :alt="article.title" class="hero-image"
                                :preload="true"
                                fallback-src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=800&fit=crop" />
                        </div>
                        <div v-else class="hero-placeholder">
                            <div class="placeholder-pattern"></div>
                        </div>
                        <!-- 渐变遮罩 -->
                        <div class="hero-gradient-overlay"></div>
                    </div>

                    <!-- 多层波浪遮罩 -->
                    <div class="wave-overlay">
                        <!-- 第一层波浪 -->
                        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" class="wave-svg wave-1">
                            <path d="M0,40 C300,60 900,10 1200,40 L1200,80 L0,80 Z" class="wave-path-1"></path>
                        </svg>
                        <!-- 第二层波浪 -->
                        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" class="wave-svg wave-2">
                            <path d="M0,50 C300,20 900,60 1200,50 L1200,80 L0,80 Z" class="wave-path-2"></path>
                        </svg>
                        <!-- 第三层波浪 -->
                        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" class="wave-svg wave-3">
                            <path d="M0,60 C300,30 900,70 1200,60 L1200,80 L0,80 Z" class="wave-path-3"></path>
                        </svg>
                    </div>

                    <!-- 半透明浮动卡片 -->
                    <div class="hero-content">
                        <div class="article-info-card">
                            <!-- 分类标签 -->
                            <div class="category-badge" v-if="category">
                                <FolderIcon :size="16" />
                                <span>{{ category.name }}</span>
                            </div>

                            <!-- 文章标题 -->
                            <h1 class="article-title">{{ article.title }}</h1>

                            <!-- 文章摘要 -->
                            <p class="article-excerpt" v-if="article.excerpt">
                                {{ article.excerpt }}
                            </p>

                            <!-- 文章元信息 -->
                            <div class="article-metadata">
                                <div class="metadata-item">
                                    <CalendarIcon :size="18" />
                                    <span>{{ formatDate(article.updated_at) }}</span>
                                </div>
                                <div class="metadata-item">
                                    <FileTextIcon :size="18" />
                                    <span>{{ article.word_count || 0 }} 字</span>
                                </div>
                                <div class="metadata-item">
                                    <ClockIcon :size="18" />
                                    <span>{{ calculateReadingTime(article.word_count) }} 分钟阅读</span>
                                </div>
                            </div>

                            <!-- 标签云 -->
                            <div class="tags-section">
                                <div class="tags-label">标签</div>
                                <div class="tags-cloud">
                                    <template v-if="tags && tags.length > 0">
                                        <span v-for="tag in tags" :key="tag.id" class="tag-item"
                                            :style="{ 'background-color': tag.color || '#10b981' }">
                                            {{ tag.name }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <span class="tag-item" style="background-color: #ef4444;">
                                            数据结构
                                        </span>
                                        <span class="tag-item" style="background-color: #3b82f6;">
                                            算法
                                        </span>
                                        <span class="tag-item" style="background-color: #8b5cf6;">
                                            编程
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <!-- 白色连接区域 -->
                <div class="content-connector"></div>

                <!-- 文章正文 -->
                <div class="article-body">

                    <!-- Markdown 内容 -->
                    <MdPreview v-if="article.article_type === 'markdown'" :modelValue="article.content" language="zh-CN"
                        theme="light" :showCodeRowNumber="true" class="markdown-content" />

                    <!-- 富文本内容 -->
                    <RichTextRenderer v-else-if="article.article_type === 'richtext'" :content="article.content"
                        class="richtext-content" />

                    <!-- 兜底：未知类型当作HTML处理 -->
                    <RichTextRenderer v-else :content="article.content" class="richtext-content" />
                </div>

                <!-- 评论区域 -->
                <CommentSection ref="commentSection" :articleId="article.id" @openAuth="openAuthModal"
                    @error="showMessage" @success="showMessage" />
            </article>

            <!-- 文章不存在提示 -->
            <div v-else class="not-found">
                <h2>文章未找到</h2>
                <p>抱歉，您要查看的文章不存在。</p>
                <button @click="goBack" class="back-btn">返回首页</button>
            </div>
        </div>

        <!-- 认证模态框已移除，现在使用独立登录页面 -->

        <!-- 简单的消息提示 -->
        <div v-if="message.text" :class="['message-toast', message.type]" @click="clearMessage">
            {{ message.text }}
        </div>
    </div>
</template>

<script>
import { MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/preview.css'
import RichTextRenderer from '../components/RichTextRenderer.vue'
import {
    FolderIcon,
    ClockIcon,
    FileTextIcon,
    TimerIcon,
    TagIcon,
    CalendarIcon
} from 'lucide-vue-next'

import { getArticleById, getAllCategories, getAllTags } from '../services/dataService.js'
import articleDataCacheService from '../services/articleDataCacheService.js'
import { useArticleCacheStore } from '../stores/articleCache.js'
import { useBrowseStateStore } from '../stores/browseState.js'
import CommentSection from '../components/CommentSection.vue'

export default {
    name: 'ArticleDetail',
    components: {
        MdPreview,
        RichTextRenderer,
        CommentSection,
        FolderIcon,
        ClockIcon,
        FileTextIcon,
        TimerIcon,
        TagIcon
    },
    props: ['id'],
    setup() {
        // 获取store实例
        const articleCacheStore = useArticleCacheStore()
        const browseStateStore = useBrowseStateStore()

        return {
            articleCacheStore,
            browseStateStore
        }
    },
    data() {
        return {
            article: null,
            category: null,
            tags: [],
            loading: true,
            error: null,
            readingProgress: 0,
            message: { text: '', type: 'info' }
        }
    },
    async mounted() {
        await this.loadArticle()
        this.setupScrollListeners()
    },
    beforeUnmount() {
        this.removeScrollListeners()
    },
    watch: {
        async id() {
            await this.loadArticle()
        }
    },
    methods: {
        async loadArticle() {
            try {
                this.loading = true
                this.error = null

                console.log(`📖 开始加载文章 ${this.id}`)

                // 使用缓存服务获取文章，优先从缓存加载
                const [article, categories, tags] = await Promise.all([
                    articleDataCacheService.getArticleDetail(this.id),
                    articleDataCacheService.getCategories(),
                    articleDataCacheService.getTags()
                ])

                if (article) {
                    this.article = article
                    console.log(`✅ 文章 ${this.id} 加载成功 ${article._cachedAt ? '(来自缓存)' : '(来自网络)'}`)

                    // 获取文章对应的分类
                    if (article.category_id) {
                        this.category = categories.find(cat => cat.id === article.category_id)
                    }

                    // 获取文章对应的标签
                    if (article.tag_ids && Array.isArray(article.tag_ids)) {
                        this.tags = tags.filter(tag => article.tag_ids.includes(tag.id))
                    }
                } else {
                    this.article = null
                    this.category = null
                    this.tags = []
                    this.error = '文章未找到'
                    console.warn(`❌ 文章 ${this.id} 未找到`)
                }
            } catch (error) {
                console.error('加载文章失败:', error)
                this.article = null
                this.category = null
                this.tags = []
                this.error = '加载文章失败，请刷新重试'
            } finally {
                this.loading = false
            }
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })
        },

        calculateReadingTime(wordCount) {
            if (!wordCount) return 1
            // 中文阅读速度大约每分钟400-500字
            const wordsPerMinute = 450
            const minutes = Math.ceil(wordCount / wordsPerMinute)
            return Math.max(minutes, 1)
        },
        goBack() {
            // 记录返回导航，用于首页状态恢复
            console.log('🔙 用户点击返回首页')
            this.$router.push('/')
        },

        setupScrollListeners() {
            window.addEventListener('scroll', this.handleScroll)
        },

        removeScrollListeners() {
            window.removeEventListener('scroll', this.handleScroll)
        },

        handleScroll() {
            this.updateReadingProgress()
        },

        updateReadingProgress() {
            const scrollTop = window.pageYOffset
            const docHeight = document.documentElement.scrollHeight - window.innerHeight
            const progress = (scrollTop / docHeight) * 100
            this.readingProgress = Math.min(Math.max(progress, 0), 100)
        },

        openAuthModal() {
            this.$router.push('/auth')
        },

        onAuthSuccess() {
            // 认证成功后，可以刷新评论组件的状态
            // 通过ref或者事件通知评论组件刷新认证状态
            this.$nextTick(() => {
                // 可以通过ref调用评论组件的方法刷新状态
                const commentSection = this.$refs.commentSection
                if (commentSection && commentSection.checkAuthStatus) {
                    commentSection.checkAuthStatus()
                }
            })
        },

        showMessage(text) {
            let type = 'info'
            if (text.includes('成功')) {
                type = 'success'
            } else if (text.includes('失败') || text.includes('错误')) {
                type = 'error'
            }

            this.message = { text, type }

            // 3秒后自动清除消息
            setTimeout(() => {
                this.clearMessage()
            }, 3000)
        },

        clearMessage() {
            this.message = { text: '', type: 'info' }
        }


    }
}
</script>

<style scoped>
.article-detail {
    min-height: 100vh;
    padding: 40px 0;
}

.article-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.back-nav {
    margin-bottom: 30px;
}

.back-btn {
    background: rgba(255, 255, 255, 0.9);
    border: none;
    padding: 12px 24px;
    border-radius: 25px;
    cursor: pointer;
    font-size: 14px;
    color: #667eea;
    font-weight: 500;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.back-btn:hover {
    background: rgba(102, 126, 234, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.loading,
.error {
    text-align: center;
    padding: 60px 40px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.loading p {
    color: #667eea;
    font-size: 1.2rem;
}

.error h2 {
    color: #e53e3e;
    margin-bottom: 15px;
    font-size: 1.8rem;
}

.article-content {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
}

/* 新的英雄区域样式 */
.article-hero {
    position: relative;
    height: 50vh;
    min-height: 400px;
    display: flex;
    align-items: flex-end;
    overflow: hidden;
    border-radius: 24px 24px 0 0;
}

.hero-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
}

.hero-image-container {
    width: 100%;
    height: 100%;
    position: relative;
}

.hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.3s ease;
}

.hero-image:hover {
    transform: scale(1.02);
}

.hero-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    position: relative;
}

.placeholder-pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 3px, transparent 3px),
        radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 3px, transparent 3px);
    background-size: 80px 80px;
    background-position: 0 0, 40px 40px;
    animation: float 20s ease-in-out infinite;
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0px);
    }

    50% {
        transform: translateY(-10px);
    }
}

.hero-gradient-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.1) 40%,
            rgba(0, 0, 0, 0.7) 100%);
    z-index: 2;
}

.hero-content {
    position: relative;
    z-index: 5;
    width: 100%;
    padding: 0 60px 60px 60px;
}

.article-info-card {
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(20px);
    border-radius: 20px;
    padding: 28px;
    max-width: 650px;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.15),
        0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: white;
    position: relative;
    transform: translateY(20px);
    animation: slideUp 0.8s ease-out forwards;
}

@keyframes slideUp {
    to {
        transform: translateY(0);
    }
}

/* 白色连接区域 */
.content-connector {
    height: 30px;
    background: #ffffff;
    position: relative;
    z-index: 1;
}

/* 多层波浪遮罩 */
.wave-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    overflow: hidden;
    z-index: 4;
    pointer-events: none;
}

.wave-svg {
    position: absolute;
    left: 0;
    width: 100%;
    height: 60px;
}

.wave-1 {
    bottom: 0;
    z-index: 3;
}

.wave-2 {
    bottom: -10px;
    z-index: 2;
}

.wave-3 {
    bottom: -20px;
    z-index: 1;
}

.wave-path-1 {
    fill: rgba(255, 255, 255, 0.95);
    animation: waveFlow1 8s ease-in-out infinite;
}

.wave-path-2 {
    fill: rgba(255, 255, 255, 0.8);
    animation: waveFlow2 10s ease-in-out infinite reverse;
}

.wave-path-3 {
    fill: rgba(255, 255, 255, 0.6);
    animation: waveFlow3 12s ease-in-out infinite;
}

@keyframes waveFlow1 {

    0%,
    100% {
        d: path('M0,40 C300,60 900,10 1200,40 L1200,80 L0,80 Z');
    }

    50% {
        d: path('M0,30 C300,50 900,5 1200,30 L1200,80 L0,80 Z');
    }
}

@keyframes waveFlow2 {

    0%,
    100% {
        d: path('M0,50 C300,20 900,60 1200,50 L1200,80 L0,80 Z');
    }

    50% {
        d: path('M0,60 C300,30 900,70 1200,60 L1200,80 L0,80 Z');
    }
}

@keyframes waveFlow3 {

    0%,
    100% {
        d: path('M0,60 C300,30 900,70 1200,60 L1200,80 L0,80 Z');
    }

    50% {
        d: path('M0,70 C300,40 900,80 1200,70 L1200,80 L0,80 Z');
    }
}

/* 分类标签 */
.category-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 16px;
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* 文章标题 */
.article-title {
    font-size: 2.5rem;
    margin-bottom: 12px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.02em;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* 文章摘要 */
.article-excerpt {
    font-size: 1.05rem;
    line-height: 1.6;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 400;
}

/* 文章元数据 */
.article-metadata {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.metadata-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
    padding: 8px 16px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    transition: all 0.2s ease;
}

.metadata-item:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
}

.metadata-item svg {
    color: #10b981;
}

.article-stats {
    display: flex;
    gap: 20px;
    margin-bottom: 25px;
    font-size: 0.9rem;
}

.word-count,
.reading-time {
    background: rgba(255, 255, 255, 0.1);
    padding: 8px 16px;
    border-radius: 16px;
    font-weight: 500;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.article-extra-info {
    display: flex;
    gap: 20px;
    font-size: 0.85rem;
    opacity: 0.9;
}

/* 标签区域 */
.tags-section {
    margin-top: 0;
}

.tags-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
}

.tags-cloud {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag-item {
    display: inline-block;
    color: white;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
    backdrop-filter: blur(10px);
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tag-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
    filter: brightness(1.1);
}

.article-body {
    padding: 50px 80px;
}

.not-found {
    text-align: center;
    padding: 60px 40px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.not-found h2 {
    color: #667eea;
    margin-bottom: 15px;
    font-size: 2rem;
}

.not-found p {
    color: #666;
    margin-bottom: 30px;
    font-size: 1.1rem;
}

/* MdPreview 组件样式调整 */
.markdown-content {
    background: transparent;
    padding: 0;
}



/* 阅读进度条 */
.reading-progress-bar {
    position: fixed;
    top: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    z-index: 1000;
    transition: width 0.3s ease;
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}



/* 响应式设计 */
@media (max-width: 1024px) {
    .article-container {
        max-width: 900px;
    }

    .article-body {
        padding: 50px 60px;
    }

    .article-meta {
        padding: 40px 60px;
    }
}

@media (max-width: 768px) {
    .article-container {
        padding: 0 15px;
        max-width: 100%;
    }

    .article-title {
        font-size: 2rem;
        margin-bottom: 25px;
    }

    .article-meta {
        padding: 30px 40px;
    }

    .article-body {
        padding: 40px;
    }

    .article-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .article-stats {
        flex-direction: column;
        gap: 12px;
        margin-bottom: 20px;
    }

    .tags {
        gap: 8px;
    }

    .category,
    .update-time,
    .word-count,
    .reading-time {
        font-size: 0.8rem;
        padding: 6px 12px;
    }

    .tag {
        font-size: 0.8rem;
        padding: 6px 12px;
    }
}



@media (max-width: 480px) {
    .article-meta {
        padding: 25px 20px;
    }

    .article-body {
        padding: 30px 20px;
    }

    .article-title {
        font-size: 1.6rem;
        margin-bottom: 20px;
    }

    .article-stats {
        gap: 10px;
    }

    .category,
    .update-time,
    .word-count,
    .reading-time,
    .tag {
        font-size: 0.75rem;
        padding: 5px 10px;
    }
}

/* 消息提示样式 */
.message-toast {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 12px 20px;
    border-radius: 8px;
    color: white;
    font-weight: 500;
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s ease;
    max-width: 300px;
    word-wrap: break-word;
}

.message-toast.success {
    background: linear-gradient(135deg, #10b981, #059669);
    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.message-toast.error {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
}

.message-toast.info {
    background: linear-gradient(135deg, #667eea, #764ba2);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.message-toast:hover {
    transform: translateY(-2px);
}

/* Lucide图标样式 */
.category-badge svg,
.metadata-item svg,
.tag-pill svg {
    flex-shrink: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .article-detail {
        padding: 20px 0;
    }

    .article-container {
        padding: 0 16px;
    }

    .article-hero {
        height: 45vh;
        min-height: 350px;
    }

    .hero-content {
        padding: 0 24px 40px 24px;
    }

    .article-info-card {
        padding: 24px 20px;
    }

    .article-title {
        font-size: 2rem;
        margin-bottom: 10px;
    }

    .article-excerpt {
        font-size: 0.95rem;
        margin-bottom: 16px;
    }

    .article-metadata {
        gap: 12px;
        margin-bottom: 16px;
        padding-bottom: 16px;
    }

    .metadata-item {
        font-size: 0.8rem;
        padding: 6px 12px;
    }

    .tags-cloud {
        gap: 6px;
    }

    .tag-item {
        font-size: 0.75rem;
        padding: 4px 8px;
    }

    .wave-overlay {
        height: 60px;
    }

    .wave-svg {
        height: 60px;
    }

    .article-body {
        padding: 40px 24px;
    }

    .back-btn {
        padding: 10px 20px;
        font-size: 13px;
    }
}

@media (max-width: 480px) {
    .article-hero {
        height: 40vh;
        min-height: 300px;
    }

    .hero-content {
        padding: 0 20px 30px 20px;
    }

    .article-info-card {
        padding: 20px 16px;
    }

    .article-title {
        font-size: 1.6rem;
    }

    .article-excerpt {
        font-size: 0.9rem;
    }

    .article-metadata {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .metadata-item {
        font-size: 0.75rem;
        padding: 6px 10px;
    }

    .wave-overlay {
        height: 50px;
    }

    .wave-svg {
        height: 50px;
    }

    .article-body {
        padding: 30px 20px;
    }
}
</style>